import type { MapPosition } from "../hooks";
import type { StoreLocatorSelectedFilter } from "../types";

export type SelectedFilterType = Record<string, StoreLocatorSelectedFilter>;

type InitialSharedFilters = {
  mapPosition?: MapPosition;
  selectedFilters?: SelectedFilterType;
};

export const URLSearchCategoryIndicator = "category_";

export const convertToURLObj = (mapPosition?: MapPosition, selectedFilters?: SelectedFilterType) => {
  const urlParamsObj: Record<string, string> = {};

  if (mapPosition) {
    if (typeof mapPosition.lat === "number") urlParamsObj.lat = mapPosition.lat.toString();
    if (typeof mapPosition.lng === "number") urlParamsObj.lng = mapPosition.lng.toString();
    if (typeof mapPosition.zoom === "number") urlParamsObj.zoom = mapPosition.zoom.toString();
  }

  if (selectedFilters) {
    const filterObj: Record<string, string[]> = {};

    for (const filter in selectedFilters) {
      const [categoryKey, filterKey] = filter.split(".");
      if (!categoryKey || !filterKey) continue;

      if (filterObj[categoryKey]) {
        filterObj[categoryKey] = [...filterObj[categoryKey], filterKey];
      } else {
        filterObj[categoryKey] = [filterKey];
      }
    }

    for (const categoryKey in filterObj) {
      urlParamsObj[`${URLSearchCategoryIndicator}${categoryKey}`] = filterObj[categoryKey].join(",");
    }
  }

  return urlParamsObj;
};

export const convertFromURLParams = (urlParams: URLSearchParams) => {
  const mapPosition: MapPosition = {};

  if (urlParams.has("lat")) {
    const lat = urlParams.get("lat");
    if (lat !== null) mapPosition.lat = parseFloat(lat);
  }
  if (urlParams.has("lng")) {
    const lng = urlParams.get("lng");
    if (lng !== null) mapPosition.lng = parseFloat(lng);
  }
  if (urlParams.has("zoom")) {
    const zoom = urlParams.get("zoom");
    if (zoom !== null) mapPosition.zoom = parseFloat(zoom);
  }

  const selectedFilters: SelectedFilterType = {};
  const searchKeys = urlParams.keys();

  for (const searchKey of searchKeys) {
    if (!searchKey.includes(URLSearchCategoryIndicator)) continue;

    const categoryKey = searchKey.replace(URLSearchCategoryIndicator, "");
    const filterKeys = urlParams.get(searchKey)?.split(",") ?? [];

    filterKeys.forEach((filterKey) => {
      selectedFilters[`${categoryKey}.${filterKey}`] = {
        key: filterKey,
        selected: true,
        category: categoryKey,
      };
    });
  }

  return { mapPosition, selectedFilters };
};

export const getInitialDataFromURL = (
  isFiltersSharingEnabled?: boolean,
  isMapPositionFromUrlEnabled = false
): InitialSharedFilters => {
  if (typeof window !== "undefined") {
    const urlParams = new URLSearchParams(window.location.search);
    const { mapPosition, selectedFilters } = convertFromURLParams(urlParams);

    return {
      mapPosition: isFiltersSharingEnabled || isMapPositionFromUrlEnabled ? mapPosition : {},
      selectedFilters: isFiltersSharingEnabled ? selectedFilters : {},
    };
  }

  return { selectedFilters: {}, mapPosition: {} };
};
